<template>
  <div>
    <base-dialog :active="displayLoginDialog" :title="t('opendialog.Open wheel')" :cancel-text="t('common.Cancel')"
      @close="enter_inactive()" @cancel="enter_inactive()">
      <div class="p-4 space-y-4">
        <p>
          {{ t('opendialog.To open wheels') }}
        </p>
        <div class="rounded-lg border border-stone-200 dark:border-stone-700 bg-white/80 dark:bg-stone-800/60 p-4">
          <div class="space-y-3">
            <button class="btn btn-primary w-full flex items-center justify-center gap-2" @click="continueWithGoogle"
              :disabled="loading">
              <i class="fab fa-google"></i>
              <span>{{ t('common.Sign in with Google') }}</span>
            </button>

            <div class="relative flex items-center">
              <div class="flex-grow border-t border-stone-200 dark:border-stone-700"></div>
              <span class="px-3 text-xs text-stone-500 dark:text-stone-400">{{ t('common.or') }}</span>
              <div class="flex-grow border-t border-stone-200 dark:border-stone-700"></div>
            </div>

            <form @submit.prevent="onSubmit" class="space-y-2">
              <input v-model="email" type="email" required :placeholder="t('common.Email') || 'Email'"
                class="w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800 px-3 py-2 text-sm" />
              <input v-model="password" type="password" required :placeholder="t('common.Password') || 'Password'"
                class="w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800 px-3 py-2 text-sm" />
              <div v-if="authMode === 'signUp'">
                <input ref="displayNameInput" v-model="displayName" type="text"
                  :aria-invalid="showDisplayNameError ? 'true' : 'false'" required
                  :placeholder="t('common.Display name') || 'Display name'"
                  class="w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-800 px-3 py-2 text-sm" />
                <p v-if="showDisplayNameError" class="mt-1 text-xs text-red-600 dark:text-red-300">
                  {{ t('common.Display name is required') }}
                </p>
              </div>
              <div class="flex items-center gap-2">
                <button v-if="authMode === 'signIn'" type="submit" class="btn btn-primary" :disabled="loading">{{
                  t('common.Sign in') }}</button>
                <button v-if="authMode === 'signIn'" type="button"
                  class="ml-auto text-primary-600 hover:text-primary-700 underline disabled:opacity-50 disabled:pointer-events-none text-sm"
                  @click="signUpEmail" :disabled="loading">
                  {{ t('common.Create account') }}
                </button>
                <template v-else>
                  <button type="submit" class="btn btn-primary" :disabled="loading">{{ t('common.Create account')
                  }}</button>
                  <button type="button"
                    class="ml-auto text-primary-600 hover:text-primary-700 underline disabled:opacity-50 disabled:pointer-events-none text-sm"
                    @click="authMode = 'signIn'" :disabled="loading">
                    {{ t('common.Sign in instead') || 'Sign in instead' }}
                  </button>
                </template>
              </div>
            </form>
          </div>
        </div>
      </div>
    </base-dialog>

    <base-dialog :active="displayWheelDialog" :title="t('opendialog.Open wheel')" :cancel-text="t('common.Close')"
      @close="enter_inactive()" @cancel="enter_inactive()">
      <template #title>
        <div class="flex items-center">
          <i class="fa fa-folder-open mr-2"></i>{{ t('opendialog.Open wheel') }}
        </div>
      </template>
      <template #header-actions>
        <button @click="openAccountFromHere" class="flex items-center" aria-label="Open account">
          <img :src="userPhotoUrl" alt="User avatar"
            class="w-8 h-8 rounded-full ring-2 ring-stone-200 dark:ring-stone-700 shadow-sm hover:ring-primary-500 transition-all">
        </button>
      </template>

      <div class="p-2">
        <table class="w-full border-collapse">
          <tbody>
            <tr v-for="wheel in validSavedWheels" :key="wheel.title"
              class="border-b border-stone-200 dark:border-stone-700">
              <td class="py-3 px-4 text-stone-800 dark:text-stone-200">{{ wheel.title }}</td>
              <td class="py-3 px-4 text-right">
                <button class="btn btn-secondary btn-sm" @click="enter_openingWheel(wheel.title)">
                  <i class="far fa-folder-open mr-1"></i>{{ t('common.Open') }}
                </button>
              </td>
              <td class="py-3 px-4 text-right" v-if="online">
                <button class="btn btn-danger btn-sm" @click="enter_confirmingDelete(wheel.title)">
                  <i class="far fa-trash-alt mr-1"></i>{{ t('common.Delete') }}
                </button>
              </td>
            </tr>
            <tr v-if="noSavedWheels">
              <td class="py-3 px-4 text-stone-600 dark:text-stone-400" colspan="3">
                {{ t('opendialog.No wheels found') }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </base-dialog>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, getCurrentInstance, watch, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWheelStore } from '@/stores/useWheelStore';
import { useUserStore } from '@/stores/useUserStore';
import { useHardwareStore } from '@/stores/useHardwareStore';
import * as Util from '@utils/Util.js';
import BaseDialog from '../shared/BaseDialog.vue';
import { onOpen as onDialogOpen } from '@/services/ui/DialogCoordinator.js';
import useAuth from '@/composables/useAuth.js';

const { t } = useI18n();
const wheelStore = useWheelStore();
const userStore = useUserStore();
const hardwareStore = useHardwareStore();
const instance = getCurrentInstance();
const auth = useAuth();

const emit = defineEmits(['start-wait-animation', 'stop-wait-animation', 'show-snackbar-message', 'reset-wheel-rotation', 'reset-address-bar', 'open-account-dialog']);

const fsm = ref('inactive');
const loading = ref(false);
const email = ref('');
const password = ref('');
const displayName = ref('');
const authMode = ref('signIn');
const showDisplayNameError = ref(false);
const displayNameInput = ref(null);
const userPhotoUrl = computed(() => userStore.getUserPhotoUrl || '/images/user_profile.png');

// Clear display-name error as the user types a valid name
watch(displayName, (val) => {
  if (showDisplayNameError.value && val && val.trim()) {
    showDisplayNameError.value = false;
  }
});

const savedWheels = computed(() => userStore.savedWheels);
const isLoggedIn = computed(() => userStore.isUserLoggedIn);
const online = computed(() => hardwareStore.isOnline);

const validSavedWheels = computed(() => {
  const wheels = Array.isArray(savedWheels.value) ? savedWheels.value : [];
  return wheels.filter(w => w && w.title && w.title.trim().length > 0);
});

const noSavedWheels = computed(() => validSavedWheels.value.length === 0);

// If user logs in while the dialog is open, refresh the list
watch(isLoggedIn, (now) => {
  if (now && fsm.value !== 'inactive') {
    userStore.loadSavedWheels().catch(() => { });
  }
});

const displayLoginDialog = computed({
  get: () => fsm.value === 'userIsPickingLoginMethod',
  set: (val) => { if (val === false) fsm.value = 'inactive'; }
});

const displayWheelDialog = computed({
  get: () => ['userIsPickingWheel', 'confirmingDelete', 'deletingWheel'].includes(fsm.value),
  set: (val) => { if (val === false) fsm.value = 'inactive'; }
});

// Ensure this dialog closes if another dialog is opened
let unsubscribeDialogOpen = null;
onMounted(() => {
  unsubscribeDialogOpen = onDialogOpen((name) => {
    if (name !== 'open') {
      fsm.value = 'inactive';
    }
  });
});
onBeforeUnmount(() => {
  if (typeof unsubscribeDialogOpen === 'function') {
    try { unsubscribeDialogOpen(); } catch { }
  }
});

const show = async () => {
  enter_loadingLibraries();
};

const enter_loadingLibraries = async () => {
  fsm.value = 'loadingLibraries';
  emit('start-wait-animation');
  let userIsLoggedIn;
  try {
    userIsLoggedIn = await userStore.userIsLoggedInAction();
    if (userIsLoggedIn) {
      enter_loadingWheels();
    } else {
      enter_userIsPickingLoginMethod();
    }
  } catch (ex) {
    enter_authError(ex);
  } finally {
    emit('stop-wait-animation');
  }
};

const enter_inactive = () => {
  fsm.value = 'inactive';
  resetDialogState();
};

const resetDialogState = () => {
  // Nothing persistent to reset currently
};

const enter_userIsPickingLoginMethod = () => {
  fsm.value = 'userIsPickingLoginMethod';
  authMode.value = 'signIn';
  displayName.value = '';
  showDisplayNameError.value = false;
};

const enter_userIsPickingWheel = () => { fsm.value = 'userIsPickingWheel'; };

const enter_loadingWheels = async () => {
  fsm.value = 'loadingWheels';
  try {
    await userStore.loadSavedWheels();
  } catch (ex) {
    enter_authError(ex);
  }
  enter_userIsPickingWheel();
};

const enter_openingWheel = (wheelTitle) => {
  fsm.value = 'openingWheel';
  const wheel = validSavedWheels.value.find(w => w.title == wheelTitle);
  if (wheel) {
    userStore.logWheelRead(wheelTitle);
    wheelStore.setWheelConfig(wheel.config);
    wheelStore.clearWinners();
    emit('reset-wheel-rotation');
    emit('reset-address-bar');
    enter_inactive();
  } else {
    console.error('Wheel not found:', wheelTitle);
    enter_inactive();
  }
};

const enter_confirmingDelete = (wheelTitle) => {
  fsm.value = 'confirmingDelete';
  const ui = instance?.appContext.config.globalProperties.$ui || window.$ui;
  ui?.dialog.confirm({
    title: t('opendialog.Delete wheel'),
    message: t('opendialog.Are you sure', { wheelTitle: Util.escapeHtml(wheelTitle) }),
    cancelText: t('common.Cancel'),
    confirmText: t('common.Delete'),
    type: 'is-danger',
    hasIcon: true,
    onConfirm: () => enter_deletingWheel(wheelTitle),
    onCancel: () => enter_userIsPickingWheel()
  });
};

const enter_authError = (exception) => {
  fsm.value = 'authError';
  userStore.logOut();
  enter_inactive();
};

const enter_deletingWheel = async (wheelTitle) => {
  fsm.value = 'deletingWheel';
  try {
    emit('start-wait-animation');
    await userStore.deleteSavedWheel(wheelTitle);
    await userStore.loadSavedWheels();
    emit('stop-wait-animation');
    enter_userIsPickingWheel();
  } catch (ex) {
    Util.trackException(ex);
    emit('stop-wait-animation');
    enter_authError(ex);
  }
};

defineExpose({ show, enter_inactive });

function openAccountFromHere() {
  // Close this dialog and signal parent to open account dialog
  enter_inactive();
  emit('open-account-dialog');
}

// New auth helpers
const continueWithGoogle = async () => {
  try {
    loading.value = true;
    Util.trackEvent('Wheel', 'LoginForOpenAttempt', 'google');
    const resultUser = await auth.signInWithGoogle();
    if (resultUser) {
      await auth.postSignInSetup();
      await enter_loadingWheels();
    }
  } catch (ex) {
    Util.trackEvent('Wheel', 'LoginForOpenFailure', 'google');
    enter_authError(ex);
  } finally {
    loading.value = false;
  }
};

const signInEmail = async () => {
  try {
    loading.value = true;
    Util.trackEvent('Wheel', 'LoginForOpenAttempt', 'email');
    await auth.signInWithEmailPassword(email.value.trim(), password.value);
    Util.trackEvent('Wheel', 'LoginForOpenSuccess', 'email');
    await auth.postSignInSetup();
    enter_loadingWheels();
  } catch (ex) {
    Util.trackEvent('Wheel', 'LoginForOpenFailure', 'email');
    if (ex && (ex.code === 'auth/user-not-found' || ex.code === 'auth/invalid-credential')) {
      authMode.value = 'signUp';
      showDisplayNameError.value = false;
      nextTick(() => displayNameInput.value && displayNameInput.value.focus && displayNameInput.value.focus());
    } else {
      enter_authError(ex);
    }
  } finally {
    loading.value = false;
  }
};

const signUpEmail = async () => {
  if (authMode.value === 'signIn') {
    authMode.value = 'signUp';
    showDisplayNameError.value = false;
    nextTick(() => displayNameInput.value && displayNameInput.value.focus && displayNameInput.value.focus());
    return;
  }
  try {
    loading.value = true;
    Util.trackEvent('Wheel', 'LoginForOpenAttempt', 'email-signup');
    if (!displayName.value || !displayName.value.trim()) {
      showDisplayNameError.value = true;
      loading.value = false;
      nextTick(() => displayNameInput.value && displayNameInput.value.focus && displayNameInput.value.focus());
      return;
    }
    await auth.registerWithEmailPassword(
      email.value.trim(),
      password.value,
      displayName.value.trim()
    );
    Util.trackEvent('Wheel', 'LoginForOpenSuccess', 'email-signup');
    await auth.postSignInSetup();
    enter_loadingWheels();
  } catch (ex) {
    Util.trackEvent('Wheel', 'LoginForOpenFailure', 'email-signup');
    enter_authError(ex);
  } finally {
    loading.value = false;
  }
};

const onSubmit = async () => {
  if (authMode.value === 'signIn') {
    await signInEmail();
  } else {
    await signUpEmail();
  }
};
</script>
